import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import InstallAgentIA from "@/components/InstallAgentIA";
import {
  Sparkles, MessageSquare, PenLine, Image as ImageIcon, FileText,
  Send, Loader2, Download, Copy, Plus, Wand2, Upload, Bot, User as UserIcon,
  Paperclip, History, Trash2, X, AlertTriangle, Zap, Megaphone, Share2, Facebook, Users,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "chat", label: "Assistant", icon: MessageSquare },
  { id: "text", label: "Rédaction", icon: PenLine },
  { id: "image", label: "Images", icon: ImageIcon },
  { id: "doc", label: "Documents", icon: FileText },
];

const MD_CLASS = "text-sm leading-relaxed space-y-1.5 [&_p]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-1.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-1.5 [&_li]:my-0.5 [&_strong]:font-semibold [&_h1]:font-bold [&_h1]:text-lg [&_h2]:font-bold [&_h2]:text-base [&_h3]:font-semibold [&_a]:text-[#E05638] [&_a]:underline [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:rounded";

function downloadB64(filename, mime, b64) {
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([arr], { type: mime }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function dataUrlToBlob(dataUrl) {
  const [h, b] = dataUrl.split(",");
  const mime = (h.match(/:(.*?);/) || [, "image/png"])[1];
  const bin = atob(b);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}
function b64ToBlob(b64, mime) {
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}
async function fetchBlob(path) {
  const r = await api.get(`/ai/files/${path}`, { responseType: "blob" });
  return r.data;
}
async function downloadFromPath(path, filename) {
  try {
    const blob = await fetchBlob(path);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Fichier indisponible, réessayez.");
  }
}

function AuthedImage({ path }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let u; let alive = true;
    setErr(false);
    fetchBlob(path).then((b) => { u = URL.createObjectURL(b); if (alive) setUrl(u); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [path]);
  if (err) return <div className="w-72 max-w-full text-xs text-slate-400 border border-dashed border-slate-300 rounded-xl p-3">Visuel momentanément indisponible.</div>;
  if (!url) return <div className="w-72 h-40 rounded-xl bg-slate-100 animate-pulse" />;
  return <img src={url} alt="Visuel généré" className="rounded-xl w-72 max-w-full border border-slate-200" />;
}

function ShareButton({ text, getBlob }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const enc = encodeURIComponent;
  const txt = (text || "").slice(0, 600);
  const share = async () => {
    try {
      if (getBlob && navigator.canShare) {
        const { blob, filename, mime } = await getBlob();
        const file = new File([blob], filename, { type: mime });
        if (navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], text: txt || filename }); return; }
      }
      if (navigator.share) { await navigator.share({ text: txt, url: origin }); return; }
    } catch { /* bascule vers le menu */ }
    setOpen((o) => !o);
  };
  const link = "block px-3 py-1.5 rounded hover:bg-slate-100 text-left";
  return (
    <div className="relative inline-block" ref={ref}>
      <button onClick={share} data-testid="ai-share-btn" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#E05638]"><Share2 size={13} /> Partager</button>
      {open && (
        <div className="absolute z-30 bottom-full mb-1 left-0 bg-white border border-slate-200 rounded-xl shadow-lg p-1 text-sm whitespace-nowrap" data-testid="ai-share-menu">
          <a target="_blank" rel="noreferrer" onClick={() => setOpen(false)} href={`https://wa.me/?text=${enc(txt + " " + origin)}`} className={link}>WhatsApp</a>
          <a target="_blank" rel="noreferrer" onClick={() => setOpen(false)} href={`https://www.facebook.com/sharer/sharer.php?u=${enc(origin)}&quote=${enc(txt)}`} className={link}>Facebook</a>
          <a target="_blank" rel="noreferrer" onClick={() => setOpen(false)} href={`https://twitter.com/intent/tweet?text=${enc(txt)}&url=${enc(origin)}`} className={link}>X / Twitter</a>
          <a onClick={() => setOpen(false)} href={`mailto:?body=${enc(txt + " " + origin)}`} className={link}>E-mail</a>
          <button onClick={() => { navigator.clipboard.writeText(txt); toast.success("Copié !"); setOpen(false); }} className={`${link} w-full`}>Copier le texte</button>
        </div>
      )}
    </div>
  );
}

function FacebookShareButton({ text, imagePath, imageDataUrl, title }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const body = { text: text || "", title: title || null, origin: BACKEND };
      if (imagePath) body.image_path = imagePath;
      else if (imageDataUrl) body.image_b64 = imageDataUrl;
      const { data } = await api.post("/ai/share", body);
      const shareUrl = `${BACKEND}/api/ai/s/${data.slug}`;
      const u = encodeURIComponent(shareUrl);
      const quote = encodeURIComponent((text || "").slice(0, 600));
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${quote}`, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} data-testid="ai-fb-share-btn"
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#1877F2] hover:bg-[#0f63d6] rounded-full px-3 py-1.5 transition-colors disabled:opacity-60">
      {busy ? <Loader2 size={13} className="animate-spin" /> : <Facebook size={13} />} Partager sur Facebook
    </button>
  );
}

function GroupShareButton({ text, imagePath, imageDataUrl, title }) {
  const [open, setOpen] = useState(false);
  const [official, setOfficial] = useState([]);
  const [mine, setMine] = useState([]);
  const [manage, setManage] = useState(false);
  const [draft, setDraft] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyUrl, setBusyUrl] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setManage(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ai/fb-groups");
      setOfficial(data.official || []);
      setMine(data.mine || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const toggle = () => {
    const n = !open; setOpen(n); setManage(false);
    if (n) load();
  };

  const publish = async (group) => {
    if (busyUrl) return;
    setBusyUrl(group.url);
    try {
      const body = { text: text || "", title: title || null, origin: BACKEND };
      if (imagePath) body.image_path = imagePath;
      else if (imageDataUrl) body.image_b64 = imageDataUrl;
      const { data } = await api.post("/ai/share", body);
      const shareUrl = `${BACKEND}/api/ai/s/${data.slug}`;
      const clip = `${(text || "").trim()}\n\n${shareUrl}`.trim();
      try { await navigator.clipboard.writeText(clip); } catch { /* clipboard indisponible */ }
      window.open(group.url, "_blank", "noopener,noreferrer");
      toast.success("Texte et visuel copiés ! Collez-les (Ctrl/Cmd + V) dans le groupe, puis publiez.", { duration: 7000 });
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyUrl(""); }
  };

  const openManage = () => { setDraft(mine.length ? mine.map((g) => ({ ...g })) : [{ name: "", url: "" }]); setManage(true); };
  const saveManage = async () => {
    const groups = draft.filter((g) => (g.url || "").trim());
    try {
      const { data } = await api.put("/ai/fb-groups", { groups });
      setMine(data.groups || []);
      if (data.rejected > 0) toast.warning(`${data.rejected} lien(s) ignoré(s) : seules les URL de groupes Facebook sont acceptées.`);
      else toast.success("Groupes enregistrés.");
      setManage(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const item = "flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-slate-100 text-left text-sm disabled:opacity-50";
  return (
    <div className="relative inline-block" ref={ref}>
      <button onClick={toggle} data-testid="ai-group-share-btn"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#5b4b8a] hover:bg-[#4a3d73] rounded-full px-3 py-1.5 transition-colors">
        <Users size={13} /> Publier dans un groupe
      </button>
      {open && (
        <div className="absolute z-40 bottom-full mb-1 left-0 w-72 max-w-[85vw] bg-white border border-slate-200 rounded-xl shadow-xl p-2 text-sm" data-testid="ai-group-menu">
          {manage ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between px-1">
                <span className="font-semibold text-slate-700">Mes groupes</span>
                <button onClick={() => setManage(false)} className="text-slate-400 hover:text-slate-700"><X size={15} /></button>
              </div>
              <div className="space-y-2 max-h-56 overflow-y-auto">
                {draft.map((g, i) => (
                  <div key={i} className="space-y-1 border border-slate-200 rounded-lg p-2">
                    <input value={g.name} onChange={(e) => setDraft((d) => d.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Nom (facultatif)" className="w-full text-xs rounded border border-slate-200 px-2 py-1" data-testid={`ai-group-name-${i}`} />
                    <div className="flex gap-1">
                      <input value={g.url} onChange={(e) => setDraft((d) => d.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} placeholder="https://www.facebook.com/groups/…" className="flex-1 text-xs rounded border border-slate-200 px-2 py-1" data-testid={`ai-group-url-${i}`} />
                      <button onClick={() => setDraft((d) => d.filter((_, j) => j !== i))} className="text-rose-500 px-1" data-testid={`ai-group-remove-${i}`}><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => setDraft((d) => [...d, { name: "", url: "" }])} className="inline-flex items-center gap-1 text-xs text-[#E05638] font-semibold px-1" data-testid="ai-group-add"><Plus size={13} /> Ajouter un groupe</button>
              <Button onClick={saveManage} className="w-full btn-shine text-white h-9" data-testid="ai-group-save">Enregistrer</Button>
            </div>
          ) : loading ? (
            <div className="py-6 grid place-items-center"><Loader2 size={18} className="animate-spin text-slate-400" /></div>
          ) : (
            <div className="space-y-1 max-h-72 overflow-y-auto">
              <p className="text-[11px] leading-snug text-slate-400 px-2 pt-1 pb-1">Le texte et le visuel sont copiés — collez-les dans le groupe qui s'ouvre, puis publiez.</p>
              {official.length > 0 && <p className="text-[11px] font-semibold text-slate-400 px-2 pt-1">Groupes ProxiReduc 28</p>}
              {official.map((g, i) => (
                <button key={`o${i}`} onClick={() => publish(g)} disabled={!!busyUrl} className={item} data-testid={`ai-group-official-${i}`}>
                  {busyUrl === g.url ? <Loader2 size={14} className="animate-spin shrink-0" /> : <Users size={14} className="text-[#5b4b8a] shrink-0" />}
                  <span className="truncate">{g.name}</span>
                </button>
              ))}
              {mine.length > 0 && <p className="text-[11px] font-semibold text-slate-400 px-2 pt-2">Mes groupes</p>}
              {mine.map((g, i) => (
                <button key={`m${i}`} onClick={() => publish(g)} disabled={!!busyUrl} className={item} data-testid={`ai-group-mine-${i}`}>
                  {busyUrl === g.url ? <Loader2 size={14} className="animate-spin shrink-0" /> : <Users size={14} className="text-slate-500 shrink-0" />}
                  <span className="truncate">{g.name || g.url}</span>
                </button>
              ))}
              <button onClick={openManage} className="flex items-center gap-1.5 w-full px-3 py-2 rounded-lg hover:bg-slate-100 text-left text-xs font-semibold text-[#E05638] border-t border-slate-100 mt-1" data-testid="ai-group-manage">
                <Plus size={13} /> Gérer mes groupes
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Disclaimer({ compact }) {
  return (
    <div className={`flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 text-amber-800 ${compact ? "px-3 py-2 text-xs" : "px-4 py-2.5 text-sm"}`} data-testid="ai-disclaimer">
      <AlertTriangle size={compact ? 14 : 16} className="shrink-0 mt-0.5" />
      <span>Les contenus (textes, images, documents) sont générés par une IA et peuvent comporter des erreurs. <strong>Vérifiez-les toujours avant utilisation ou diffusion.</strong></span>
    </div>
  );
}

/* ------------------------------- Assistant (chat) ------------------------------- */
function ChatPanel() {
  const [messages, setMessages] = useState([]);
  const [cid, setCid] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [attached, setAttached] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [choiceUsed, setChoiceUsed] = useState({});
  const endRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);
  useEffect(() => { loadConversations(); }, []);

  const loadConversations = async () => {
    try {
      const { data } = await api.get("/ai/conversations");
      setConversations(data.conversations || []);
    } catch { /* silencieux */ }
  };

  const openConversation = async (id) => {
    setShowHistory(false);
    try {
      const { data } = await api.get(`/ai/conversations/${id}/messages`);
      setMessages((data.messages || []).map((m) => ({ role: m.role, content: m.content, image: m.image || null, document: m.document || null, doc_choice: m.doc_choice || false })));
      setCid(id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeConversation = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer définitivement cette discussion ?")) return;
    try {
      await api.delete(`/ai/conversations/${id}`);
      setConversations((c) => c.filter((x) => x.id !== id));
      if (id === cid) reset();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const reset = () => { setMessages([]); setCid(null); setInput(""); setAttached(null); if (fileRef.current) fileRef.current.value = ""; };

  const sendChoice = (prompt, label) => { if (prompt) send(`${prompt} (au format ${label})`); };

  // Question saisie dans la démo d'accueil (avant inscription/connexion) → envoi automatique au 1er affichage.
  // TTL 60 min pour éviter qu'une question oubliée ne soit renvoyée plus tard pour un autre utilisateur.
  useEffect(() => {
    const raw = localStorage.getItem("proxi_pending_ai_question");
    localStorage.removeItem("proxi_pending_ai_question");
    if (raw) {
      try {
        const { q, ts } = JSON.parse(raw);
        if (q && ts && Date.now() - ts < 60 * 60 * 1000) send(q);
      } catch { /* format invalide : on ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (override) => {
    const msg = (typeof override === "string" ? override : input).trim();
    if ((!msg && !attached) || busy) return;
    const label = attached ? `📎 ${attached.name}${msg ? "\n" + msg : ""}` : msg;
    setMessages((m) => [...m, { role: "user", content: label }]);
    setInput("");
    setBusy(true);
    const sentFile = attached;
    setAttached(null);
    try {
      let data;
      if (sentFile) {
        const fd = new FormData();
        fd.append("message", msg);
        if (cid) fd.append("conversation_id", cid);
        fd.append("file", sentFile);
        ({ data } = await api.post("/ai/chat/file", fd, { headers: { "Content-Type": "multipart/form-data" } }));
      } else {
        ({ data } = await api.post("/ai/chat", { message: msg, conversation_id: cid }));
      }
      setCid(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, image: data.image || null, document: data.document || null, doc_choice: data.doc_choice || false }]);
      loadConversations();
    } catch (e) {
      toast.error(formatApiError(e));
      setMessages((m) => m.slice(0, -1));
      setInput(msg);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const convListEl = (
    <>
      <Button variant="outline" size="sm" onClick={reset} className="w-full justify-start mb-2" data-testid="ai-chat-new"><Plus size={15} className="mr-1" /> Nouvelle discussion</Button>
      <div className="space-y-1 overflow-y-auto min-w-0">
        {conversations.length === 0 && <p className="text-xs text-slate-400 px-2 py-1">Aucune discussion pour l'instant.</p>}
        {conversations.map((c) => (
          <div key={c.id} onClick={() => openConversation(c.id)} data-testid="ai-conv-item"
            className={`group flex items-center gap-1 px-2.5 py-2 rounded-lg cursor-pointer text-sm min-w-0 ${c.id === cid ? "bg-orange-50 text-[#E05638]" : "hover:bg-slate-100 text-slate-700"}`}>
            <MessageSquare size={14} className="shrink-0 opacity-60" />
            <span className="truncate flex-1 min-w-0">{c.title}</span>
            <button onClick={(e) => removeConversation(c.id, e)} className="shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 text-slate-400 hover:text-rose-500" data-testid="ai-conv-delete"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div className="flex gap-4 h-[62vh] min-h-[440px] min-w-0" data-testid="ai-chat-panel">
      <aside className="hidden md:flex flex-col w-56 shrink-0 border border-slate-200 rounded-2xl p-2" data-testid="ai-conv-sidebar">
        {convListEl}
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden mb-2 flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowHistory((s) => !s)} data-testid="ai-history-toggle"><History size={15} className="mr-1" /> Historique</Button>
          <Button variant="outline" size="sm" onClick={reset}><Plus size={15} className="mr-1" /> Nouvelle</Button>
        </div>
        {showHistory && (
          <div className="md:hidden border border-slate-200 rounded-2xl p-2 mb-2 max-h-52 overflow-y-auto min-w-0 max-w-full">{convListEl}</div>
        )}

        <div className="flex-1 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4">
          {messages.length === 0 && (
            <div className="h-full grid place-items-center text-center text-slate-400">
              <div>
                <Bot size={40} className="mx-auto mb-2 text-[#E05638]" />
                <p className="text-sm">Bonjour 👋 Demandez un texte, une image, un document (Excel, Word, PDF…), ou joignez un fichier (📎) à analyser.</p>
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${m.role}`}>
              <span className={`w-8 h-8 rounded-full grid place-items-center shrink-0 ${m.role === "user" ? "bg-slate-900 text-white" : "bg-[#E05638] text-white"}`}>
                {m.role === "user" ? <UserIcon size={16} /> : <Bot size={16} />}
              </span>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-slate-900 text-white whitespace-pre-wrap" : "bg-white border border-slate-200 text-slate-800"}`}>
                {m.role === "user" ? m.content : <div className={MD_CLASS}><ReactMarkdown>{m.content}</ReactMarkdown></div>}
                {m.image && (
                  <div className="mt-2" data-testid="ai-msg-image">
                    <AuthedImage path={m.image} />
                    <button onClick={() => downloadFromPath(m.image, "visuel-proxireduc.jpg")} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-[#E05638] hover:underline"><Download size={12} /> Télécharger le visuel</button>
                  </div>
                )}
                {m.document && (
                  <button onClick={() => downloadFromPath(m.document.path, m.document.filename)} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#E05638] bg-orange-50 hover:bg-orange-100 rounded-full px-3 py-1.5" data-testid="ai-msg-document"><Download size={13} /> {m.document.filename}</button>
                )}
                {m.doc_choice && (
                  <div className="mt-2 flex gap-2" data-testid="ai-doc-choice">
                    <button disabled={choiceUsed[i]} onClick={() => { setChoiceUsed((c) => ({ ...c, [i]: true })); sendChoice(messages[i - 1]?.content, "Word"); }} className="text-xs font-semibold bg-[#E05638] text-white rounded-full px-3 py-1.5 disabled:opacity-50" data-testid="ai-doc-choice-word">Word (.docx)</button>
                    <button disabled={choiceUsed[i]} onClick={() => { setChoiceUsed((c) => ({ ...c, [i]: true })); sendChoice(messages[i - 1]?.content, "PDF"); }} className="text-xs font-semibold bg-slate-800 text-white rounded-full px-3 py-1.5 disabled:opacity-50" data-testid="ai-doc-choice-pdf">PDF</button>
                  </div>
                )}
                {m.role !== "user" && !m.doc_choice && (m.content || m.image || m.document) && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <FacebookShareButton text={m.content} imagePath={m.image || undefined} />
                    <GroupShareButton text={m.content} imagePath={m.image || undefined} />
                    <ShareButton text={m.content} getBlob={m.image ? (async () => ({ blob: await fetchBlob(m.image), filename: "visuel-proxireduc.jpg", mime: "image/jpeg" })) : m.document ? (async () => ({ blob: await fetchBlob(m.document.path), filename: m.document.filename, mime: m.document.mime })) : undefined} />
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex gap-2.5" data-testid="ai-chat-typing">
              <span className="w-8 h-8 rounded-full grid place-items-center bg-[#E05638] text-white shrink-0"><Bot size={16} /></span>
              <div className="rounded-2xl px-4 py-3 bg-white border border-slate-200"><Loader2 size={16} className="animate-spin text-slate-400" /></div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        {attached && (
          <div className="mt-2 inline-flex items-center gap-2 text-xs bg-orange-50 text-[#E05638] rounded-full px-3 py-1.5 w-fit" data-testid="ai-chat-attachment">
            <Paperclip size={13} /> {attached.name}
            <button onClick={() => { setAttached(null); if (fileRef.current) fileRef.current.value = ""; }}><X size={13} /></button>
          </div>
        )}
        <div className="mt-2 flex items-end gap-2">
          <input ref={fileRef} type="file" className="hidden" data-testid="ai-chat-file"
            accept="image/*,.pdf,.docx,.xlsx,.xlsm,.txt,.csv,.md,.json"
            onChange={(e) => setAttached(e.target.files?.[0] || null)} />
          <Button variant="outline" className="h-11 px-3" onClick={() => fileRef.current?.click()} data-testid="ai-chat-attach-btn" title="Joindre un fichier"><Paperclip size={18} /></Button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={2}
            placeholder="Écrivez votre message… (Entrée pour envoyer)"
            className="flex-1 resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E05638]/40"
            data-testid="ai-chat-input"
          />
          <Button onClick={send} disabled={busy || (!input.trim() && !attached)} className="btn-shine text-white h-11 px-4" data-testid="ai-chat-send">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Rédaction (texte) ------------------------------- */
const TEXT_KINDS = [
  { id: "email", label: "E-mail" },
  { id: "annonce", label: "Petite annonce" },
  { id: "article", label: "Article" },
  { id: "social", label: "Post réseaux sociaux" },
  { id: "resume", label: "Résumé" },
  { id: "custom", label: "Libre" },
];

const QUICK_TEMPLATES = [
  { label: "Répondre à un avis", kind: "custom", prompt: "Rédige une réponse polie, professionnelle et chaleureuse à cet avis client :\n\n[collez l'avis ici]" },
  { label: "Annoncer une promo", kind: "social", prompt: "Annonce une promotion pour mon commerce. Détails de l'offre :\n\n[ex. -20% sur tout le magasin ce week-end]" },
  { label: "Relancer un client", kind: "email", prompt: "Rédige un e-mail de relance commerciale courtois pour :\n\n[ex. un devis envoyé il y a 2 semaines]" },
  { label: "Décrire un produit", kind: "annonce", prompt: "Rédige une description attractive et vendeuse pour :\n\n[nom et caractéristiques du produit]" },
  { label: "Inviter à un événement", kind: "email", prompt: "Rédige une invitation engageante à l'événement suivant :\n\n[nom, date, lieu, programme]" },
  { label: "Rédiger un communiqué", kind: "article", prompt: "Rédige un court communiqué pour annoncer :\n\n[la nouvelle à communiquer]" },
];

function TextPanel() {
  const navigate = useNavigate();
  const [kind, setKind] = useState("email");
  const [tone, setTone] = useState("");
  const [prompt, setPrompt] = useState("");
  const [result, setResult] = useState("");
  const [resultKind, setResultKind] = useState(null);
  const [busy, setBusy] = useState(false);
  const [savingAnnonce, setSavingAnnonce] = useState(false);
  const promptRef = useRef(null);

  const applyTemplate = (t) => {
    setKind(t.kind);
    setPrompt(t.prompt);
    setTimeout(() => promptRef.current?.focus(), 50);
  };

  const run = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true); setResult("");
    try {
      const { data } = await api.post("/ai/text", { kind, prompt, tone: tone || undefined });
      setResult(data.text);
      setResultKind(kind);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(result); toast.success("Texte copié !"); }
    catch { toast.error("Impossible de copier le texte."); }
  };

  const saveToAnnonces = async () => {
    setSavingAnnonce(true);
    const stripMd = (s) => s.replace(/\*\*|\*|__|`|^#+\s?/gm, "").trim();
    let draft;
    try {
      const { data } = await api.post("/ai/annonce-extract", { text: result });
      draft = data;
    } catch {
      const lines = result.split("\n");
      const firstIdx = lines.findIndex((l) => l.trim());
      const title = (firstIdx >= 0 ? stripMd(lines[firstIdx]).replace(/^titre\s*[:：-]\s*/i, "") : "").slice(0, 80);
      const description = stripMd(lines.slice(firstIdx + 1).join("\n")).trim() || stripMd(result);
      draft = { title, description };
    }
    try { localStorage.setItem("annonce_ai_draft", JSON.stringify(draft)); } catch { /* ignore */ }
    setSavingAnnonce(false);
    navigate("/petites-annonces/deposer");
  };

  return (
    <div className="space-y-4" data-testid="ai-text-panel">
      <div>
        <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><Zap size={13} className="text-[#E05638]" /> Modèles rapides</label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {QUICK_TEMPLATES.map((t, i) => (
            <button key={i} onClick={() => applyTemplate(t)} data-testid={`ai-quick-${i}`}
              className="px-3 py-1.5 rounded-full text-sm border border-[#E05638]/30 bg-orange-50 text-[#E05638] hover:bg-orange-100 transition-colors">
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Type de texte</label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {TEXT_KINDS.map((k) => (
            <button key={k.id} onClick={() => setKind(k.id)} data-testid={`ai-text-kind-${k.id}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${kind === k.id ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
              {k.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Ton (facultatif)</label>
        <input value={tone} onChange={(e) => setTone(e.target.value)} placeholder="Ex. chaleureux, professionnel, humoristique…"
          className="w-full mt-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E05638]/40" data-testid="ai-text-tone" />
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Votre demande</label>
        <textarea ref={promptRef} value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={5}
          placeholder="Décrivez ce que vous voulez rédiger…"
          className="w-full mt-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E05638]/40" data-testid="ai-text-prompt" />
      </div>
      <Button onClick={run} disabled={busy || !prompt.trim()} className="btn-shine text-white" data-testid="ai-text-generate">
        {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Wand2 size={16} className="mr-2" />} Générer le texte
      </Button>
      {result && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 relative" data-testid="ai-text-result">
          <Button variant="outline" size="sm" onClick={copy} className="absolute top-3 right-3" data-testid="ai-text-copy"><Copy size={14} className="mr-1" /> Copier</Button>
          <div className={`${MD_CLASS} text-slate-800 pr-24`} data-testid="ai-text-result-content"><ReactMarkdown>{result}</ReactMarkdown></div>
        </div>
      )}
      {result && (
        <div className="-mt-1 flex flex-wrap items-center gap-2">
          <FacebookShareButton text={result} />
          <GroupShareButton text={result} />
          <ShareButton text={result} />
        </div>
      )}
      {result && resultKind === "annonce" && (
        <Button variant="outline" onClick={saveToAnnonces} disabled={savingAnnonce} data-testid="ai-save-annonce">
          {savingAnnonce ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Megaphone size={15} className="mr-2" />} Enregistrer dans mes annonces
        </Button>
      )}
    </div>
  );
}

/* ------------------------------- Images ------------------------------- */
function ImagePanel() {
  const [prompt, setPrompt] = useState("");
  const [img, setImg] = useState("");
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const generate = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true); setImg("");
    try {
      let data;
      if (file) {
        const fd = new FormData();
        fd.append("prompt", prompt);
        fd.append("file", file);
        ({ data } = await api.post("/ai/image/edit", fd, { headers: { "Content-Type": "multipart/form-data" } }));
      } else {
        ({ data } = await api.post("/ai/image", { prompt }));
      }
      setImg(data.image);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="ai-image-panel">
      <div>
        <label className="text-xs font-semibold text-slate-600">Décrivez l'image souhaitée</label>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
          placeholder="Ex. une affiche conviviale pour un vide-grenier à Chartres, style illustré, couleurs chaudes"
          className="w-full mt-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E05638]/40" data-testid="ai-image-prompt" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ai-image-file" />
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="ai-image-edit-btn"><Upload size={14} className="mr-1" /> {file ? "Changer l'image" : "Retoucher une image"}</Button>
        {file && <span className="text-xs text-slate-500 truncate max-w-[200px]">{file.name} <button className="text-rose-500 ml-1" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}>×</button></span>}
      </div>
      <Button onClick={generate} disabled={busy || !prompt.trim()} className="btn-shine text-white" data-testid="ai-image-generate">
        {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Wand2 size={16} className="mr-2" />} {file ? "Retoucher l'image" : "Générer l'image"}
      </Button>
      {busy && <p className="text-xs text-slate-400">Création en cours, cela peut prendre quelques secondes…</p>}
      {img && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="ai-image-result">
          <img src={img} alt="Image générée" className="w-full max-w-lg rounded-xl mx-auto" />
          <div className="text-center">
            <a href={img} download={`image-proxireduc.${(img.match(/^data:image\/([a-z]+)/)?.[1] || "png").replace("jpeg", "jpg")}`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E05638] hover:underline"><Download size={15} /> Télécharger</a>
            <div className="mt-1 flex flex-wrap justify-center items-center gap-2">
              <FacebookShareButton imageDataUrl={img} text="Visuel créé avec ProxiReduc 28" />
              <GroupShareButton imageDataUrl={img} text="Visuel créé avec ProxiReduc 28" />
              <ShareButton getBlob={async () => ({ blob: dataUrlToBlob(img), filename: `image-proxireduc.${((img.match(/^data:image\/([a-z]+)/) || [, "png"])[1]).replace("jpeg", "jpg")}`, mime: (img.match(/^data:(image\/[a-z]+)/) || [, "image/png"])[1] })} /></div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Documents ------------------------------- */
const DOC_FORMATS = [
  { id: "docx", label: "Word (.docx)" },
  { id: "xlsx", label: "Excel (.xlsx)" },
  { id: "pptx", label: "PowerPoint (.pptx)" },
  { id: "pdf", label: "PDF (.pdf)" },
];

function DocPanel() {
  const [format, setFormat] = useState("docx");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const run = async () => {
    if (!prompt.trim() || busy) return;
    setBusy(true); setLast(null);
    try {
      const { data } = await api.post("/ai/document", { format, prompt });
      downloadB64(data.filename, data.mime, data.b64);
      setLast(data);
      toast.success("Document généré et téléchargé !");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="ai-doc-panel">
      <div>
        <label className="text-xs font-semibold text-slate-600">Format du document</label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {DOC_FORMATS.map((f) => (
            <button key={f.id} onClick={() => setFormat(f.id)} data-testid={`ai-doc-format-${f.id}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${format === f.id ? "bg-[#E05638] text-white border-[#E05638]" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-slate-600">Décrivez le document voulu</label>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={4}
          placeholder="Ex. un budget prévisionnel pour la fête du club avec postes de dépenses et recettes"
          className="w-full mt-1.5 rounded-xl border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E05638]/40" data-testid="ai-doc-prompt" />
      </div>
      <Button onClick={run} disabled={busy || !prompt.trim()} className="btn-shine text-white" data-testid="ai-doc-generate">
        {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Download size={16} className="mr-2" />} Générer & télécharger
      </Button>
      {last && (
        <div className="space-y-1" data-testid="ai-doc-done">
          <p className="text-sm text-emerald-700">✅ {last.filename} téléchargé. Pensez à le relire avant de l'utiliser.</p>
          <ShareButton text={`Document : ${last.filename}`} getBlob={async () => ({ blob: b64ToBlob(last.b64, last.mime), filename: last.filename, mime: last.mime })} />
        </div>
      )}
    </div>
  );
}

/* ------------------------------- Page ------------------------------- */
export default function AiAgent({ hideHeader = false }) {
  const [tab, setTab] = useState("chat");
  return (
    <div className="max-w-3xl w-full min-w-0" data-testid="ai-agent-page">
      {!hideHeader && (
      <div className="flex items-center gap-3 mb-3">
        <span className="w-11 h-11 rounded-2xl bg-[#E05638] text-white grid place-items-center shadow-lg shadow-[#E05638]/30"><Sparkles size={22} /></span>
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900">Mon Agent Intelligence Artificielle</h1>
          <p className="text-sm text-slate-500">Rédigez, analysez vos fichiers, créez des images et vos documents — en quelques secondes.</p>
        </div>
      </div>
      )}

      <InstallAgentIA />

      <Disclaimer />

      <div className="flex flex-wrap gap-1.5 mt-5 mb-5 bg-slate-100 p-1 rounded-xl w-fit">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`ai-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t.id ? "bg-white text-[#E05638] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <t.icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "chat" && <ChatPanel />}
      {tab === "text" && <TextPanel />}
      {tab === "image" && <ImagePanel />}
      {tab === "doc" && <DocPanel />}
    </div>
  );
}
